import type { SessionSummary } from '@/lib/types/consultation';
import type {
  CaseSheetEditableFields,
  SimplifiedMedication,
} from '@/components/views/CaseSheetView';

/** Wire shape of the `payload` sent to POST /v2/api/case-sheet-summaries/fhir. */
export type FhirSummaryPayload = Record<string, unknown>;

/**
 * Medication in the shape the backend's FHIR bundle builder expects. It differs
 * from the frontend's `StructuredMedication` in three ways — snake_case keys,
 * `route` is named `intake_type`, and `dosage` is split into value + unit — so
 * it has to be converted rather than passed through.
 */
interface FhirMedicationItem {
  medication: string;
  dosage: { value: string; unit: string };
  frequency: string;
  timing: string[];
  duration: string;
  medicine_type: string;
  intake_type: string;
  relation_with_meal: string;
  composition: string;
  additional_info: string;
}

/**
 * Splits the flattened dosage the UI holds ("500 mg") back into the
 * value/unit pair the backend reads — it does `parseFloat(dosage.value)` and
 * puts `dosage.unit` into a UCUM-coded Quantity, so a single string here
 * would produce a NaN dose.
 */
function splitDosage(dosage: string | undefined): { value: string; unit: string } {
  const raw = (dosage ?? '').trim();
  const match = /^([\d.]+)\s*(.*)$/.exec(raw);
  if (!match) return { value: '', unit: raw };
  return { value: match[1], unit: match[2].trim() };
}

function toFhirMedication(med: SimplifiedMedication): FhirMedicationItem {
  return {
    medication: med.medication,
    dosage: splitDosage(med.dosage),
    frequency: med.frequency ?? '',
    timing: med.timing ?? [],
    duration: med.duration ?? '',
    medicine_type: med.medicineType ?? '',
    intake_type: med.route ?? '',
    relation_with_meal: med.relationWithMeal ?? '',
    composition: med.composition ?? '',
    additional_info: med.additionalInfo ?? '',
  };
}

/** Plain text of an HTML/markdown-ish string, whitespace-collapsed, for comparing edits. */
function toComparableText(raw: string): string {
  const doc = new DOMParser().parseFromString(
    raw.replace(/<\/(li|p|div)>|<br\s*\/?>/gi, '\n'),
    'text/html'
  );
  return (doc.body.textContent || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
}

/** Plain text of an edited HTML string, one line per block (for storing back as text). */
function htmlToPlainLines(html: string): string {
  const doc = new DOMParser().parseFromString(
    html.replace(/<\/(li|p|div)>|<br\s*\/?>/gi, '\n'),
    'text/html'
  );
  return (doc.body.textContent || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** Row shape the visit editor produces for a medication (see PatientRecordView). */
interface EditedMedicationRow {
  medication?: string;
  dosage?: string;
  frequency?: string;
  route?: string;
  duration?: string;
  relation_with_meal?: string;
  additional_info?: string;
  medicine_type?: string;
  composition?: string;
  item_code?: string;
  timing?: string[];
  /** Index of the saved entry this row came from; absent for rows the doctor added. */
  original_index?: number;
}

const text = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const dosageText = (dosage: unknown): string => {
  const d = asRecord(dosage);
  return d ? `${d.value ?? ''} ${d.unit ?? ''}`.trim() : text(dosage);
};

const timingText = (timing: unknown): string =>
  Array.isArray(timing) ? timing.map(text).filter(Boolean).join(', ') : text(timing);

/**
 * Merges an edited table row over the saved medication it came from. Only the
 * columns the doctor actually changed are written, so everything the table doesn't
 * show — `dosing_type`, `sos_trigger`, `max_per_day`, provenance — survives, for
 * edited and untouched medications alike.
 */
function mergeMedicationRow(
  row: EditedMedicationRow,
  saved: Record<string, unknown>
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...saved };
  const setIfChanged = (key: string, edited: unknown, shown: unknown) => {
    if (text(edited) !== text(shown)) merged[key] = text(edited);
  };

  const savedName = text(saved.medication ?? saved.name ?? saved.drug_name);
  const shownName = saved.unit ? `${savedName} ${text(saved.unit)}` : savedName;
  if (text(row.medication) !== shownName) {
    merged.medication = text(row.medication);
    delete merged.unit;
  }

  if (dosageText(row.dosage) !== dosageText(saved.dosage ?? saved.dose ?? saved.strength)) {
    merged.dosage = splitDosage(row.dosage);
  }
  setIfChanged('frequency', row.frequency, saved.frequency ?? saved.period ?? saved.schedule);
  setIfChanged('intake_type', row.route, saved.route ?? saved.intake_type);
  setIfChanged('duration', row.duration, saved.duration ?? saved.days);
  setIfChanged(
    'relation_with_meal',
    row.relation_with_meal,
    saved.relation_with_meal ?? saved.relationWithMeal
  );
  setIfChanged('medicine_type', row.medicine_type, saved.medicine_type ?? saved.medicineType);
  setIfChanged('composition', row.composition, saved.composition);
  setIfChanged('item_code', row.item_code, saved.item_code ?? saved.itemCode);
  if (timingText(row.timing) !== timingText(saved.timing)) merged.timing = row.timing ?? [];

  // The table shows an SOS medication's trigger / daily cap folded into Comments
  // (see extractMedications) — take that note back out so it isn't saved as free text.
  const savedNote = text(saved.additional_info ?? saved.additionalInfo ?? saved.comments);
  let sosNote = '';
  if (text(saved.dosing_type).toLowerCase() === 'sos') {
    const parts = [
      text(saved.sos_trigger) ? `if ${text(saved.sos_trigger)}` : '',
      text(saved.max_per_day) ? `max ${text(saved.max_per_day)}` : '',
    ].filter(Boolean);
    sosNote = parts.length > 0 ? `SOS — ${parts.join(', ')}` : 'SOS';
  }
  const shownNote = sosNote ? (savedNote ? `${savedNote}; ${sosNote}` : sosNote) : savedNote;
  const editedNote = text(row.additional_info);
  if (editedNote !== shownNote) {
    merged.additional_info =
      sosNote && editedNote === sosNote
        ? ''
        : sosNote && editedNote.endsWith(`; ${sosNote}`)
          ? editedNote.slice(0, -(sosNote.length + 2))
          : editedNote;
  }

  return merged;
}

/** A medication the doctor added in the editor — no saved entry to merge over. */
function newMedication(row: EditedMedicationRow): Record<string, unknown> {
  return {
    medication: row.medication ?? '',
    dosage: splitDosage(row.dosage),
    frequency: row.frequency ?? '',
    timing: row.timing ?? [],
    duration: row.duration ?? '',
    medicine_type: row.medicine_type ?? '',
    intake_type: row.route ?? '',
    relation_with_meal: row.relation_with_meal ?? '',
    composition: row.composition ?? '',
    additional_info: row.additional_info ?? '',
  };
}

/** Separator-insensitive form of a list, so "a, b" and "a\nb" compare equal. */
const listSignature = (items: string[]): string =>
  items
    .join(' ')
    .replace(/[\n,]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * Applies the visit editor's output to a saved FHIR summary (DocumentSource visit).
 *
 * The editor hands back a flat key → value object built for the DiagnosticReport
 * conclusion (HTML strings, medications as a bare array of UI rows). The backend's
 * FHIR builder expects the summary shape instead — `{ value, ...provenance }`
 * per field, medications with a split dosage and `intake_type`. So only the keys
 * the doctor actually changed are converted and overwritten; everything else keeps
 * the original value and its provenance.
 */
export function applyVisitEditToSummary(
  original: Record<string, unknown>,
  edited: Record<string, unknown>
): FhirSummaryPayload {
  const payload: FhirSummaryPayload = { ...original };

  for (const [key, editedValue] of Object.entries(edited)) {
    const originalField = original[key];
    const originalObj = asRecord(originalField);
    const originalValue = originalObj ? originalObj.value : originalField;

    if (Array.isArray(editedValue)) {
      const rows = editedValue as EditedMedicationRow[];
      const savedList = Array.isArray(originalValue) ? originalValue : [];
      const mergedList = rows.map((row) => {
        const saved =
          row.original_index !== undefined ? asRecord(savedList[row.original_index]) : null;
        return saved ? mergeMedicationRow(row, saved) : newMedication(row);
      });
      if (JSON.stringify(mergedList) === JSON.stringify(savedList)) continue;
      payload[key] = { ...(originalObj ?? {}), value: mergedList };
      continue;
    }

    const editedRaw = asRecord(editedValue)?.value ?? editedValue;
    if (typeof editedRaw !== 'string') continue;

    if (Array.isArray(originalValue)) {
      // Lists of objects (family history, examination, diagnoses…) can't be rebuilt
      // from text; the editor keeps those read-only, so leave them exactly as saved.
      if (!originalValue.every((item) => typeof item === 'string')) continue;
      // A list of plain strings (e.g. investigations, allergies) is edited as text.
      // The editor wraps it in markup, so decode that first, and keep one item per line.
      let editedItems = htmlToPlainLines(editedRaw).split('\n').filter(Boolean);
      if (listSignature(editedItems) === listSignature(originalValue as string[])) continue;
      // A single comma-joined line is the older way of writing several items.
      if (editedItems.length === 1 && originalValue.length > 1) {
        editedItems = editedItems[0]
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }
      payload[key] = { ...(originalObj ?? {}), value: editedItems };
      continue;
    }

    if (
      typeof originalValue === 'string' &&
      toComparableText(originalValue) === toComparableText(editedRaw)
    ) {
      continue;
    }
    if (originalValue === undefined && !toComparableText(editedRaw)) continue;

    const plain = htmlToPlainLines(editedRaw);
    payload[key] = originalObj ? { ...originalObj, value: plain } : { value: plain };
  }

  return payload;
}

/** The free-text sections: payload key → where that text lives on the Case Sheet. */
const TEXT_SECTIONS: Array<
  [key: string, read: (fields: CaseSheetEditableFields) => string | undefined]
> = [
  ['patient_summary', (f) => f.patientSummary],
  ['chief_complaint', (f) => f.chiefComplaint],
  ['history_of_present_illness', (f) => f.clinicalSymptoms],
  ['past_medical_surgical_history', (f) => f.medicalHistory],
  ['medication_history', (f) => f.medicationHistory],
  ['social_history', (f) => f.socialHistory],
  ['review_of_systems', (f) => f.reviewOfSystems],
  ['treatment_plan_management', (f) => f.treatmentPlan],
  ['follow_up_plan', (f) => f.followUpPlan || f.doctorNotes],
  ['meal_plan', (f) => f.dietAdvice],
  ['pediatric_milestone', (f) => f.pediatricMilestone],
  ['adt_order', (f) => f.adtOrder],
];

/**
 * Every section the backend validates as a `{ value, ...provenance }` object
 * (summaryFieldSchema / hpiFieldSchema in the case-sheet-summary controller).
 */
const SUMMARY_FIELD_KEYS = [
  'chief_complaint',
  'history_of_present_illness',
  'past_medical_surgical_history',
  'medication_history',
  'allergies',
  'family_history',
  'social_history',
  'review_of_systems',
  'physical_examination',
  'assessment_and_diagnosis',
  'investigations_orders',
  'treatment_plan_management',
  'medications',
  'follow_up_plan',
  'meal_plan',
  'pediatric_milestone',
  'adt_order',
  'patient_summary',
];

/**
 * The Ayurveda agent's summary can hold a section as a bare string / array
 * instead of `{ value }`, and the backend rejects that with a 400 ("Expected
 * object, received string"). Fields the doctor didn't edit are forwarded from
 * the summary as they are, so wrap those here; objects (which already carry
 * provenance) are left exactly as they are.
 */
function wrapBareSummaryFields(payload: FhirSummaryPayload): FhirSummaryPayload {
  for (const key of SUMMARY_FIELD_KEYS) {
    const value = payload[key];
    if (typeof value === 'string' || Array.isArray(value)) {
      payload[key] = { value };
    }
  }
  return payload;
}

const namedMedications = (fields: CaseSheetEditableFields): SimplifiedMedication[] =>
  (fields.prescribedMedications ?? []).filter((med) => med.medication?.trim());

/**
 * Builds the payload for the FHIR save from what the doctor is actually
 * looking at on the Case Sheet.
 *
 * The AI's `sessionSummary` is the base. `baseline` is the Case Sheet exactly as
 * it was first built from that summary, which is what tells an edit from a
 * field the doctor never touched:
 *
 * - **untouched** (current value equals the baseline): the AI's original value
 *   is kept *with* its provenance (timestamp / source_phrase / explanation),
 *   which the backend attaches to each generated resource as an
 *   `ai-source-metadata` extension.
 * - **edited**: replaced with the doctor's value, which carries no provenance
 *   because it is no longer AI-derived. Clearing a field is an edit too — it
 *   is sent as an empty value, so the AI's text isn't left standing in the
 *   record after the doctor deleted it.
 * - **no AI value to keep** (e.g. the page was refreshed and there is no
 *   `sessionSummary`): whatever is on screen is sent, so a Composition is
 *   always written rather than the save silently doing nothing.
 *
 * Only fields the Case Sheet can represent faithfully are overridden — the
 * free-text boxes, plus the medications table. The remaining sections
 * (allergies, family history, physical examination, diagnoses, investigations)
 * are structured arrays server-side, and the Case Sheet edits them as plain
 * text; parsing that text back into those structures would be guesswork, and
 * the builder silently drops anything that isn't already the right shape. Those
 * pass through from the AI untouched — a doctor's edit to them still persists
 * via the DB summary and the raw DocumentReference, just not as separate FHIR
 * resources.
 */
export function buildFhirSummaryPayload(
  fields: CaseSheetEditableFields,
  baseline: CaseSheetEditableFields,
  sessionSummary?: SessionSummary,
  fallbackSymptomCodes?: Array<{ symptom: string; symptom_id: string }>
): FhirSummaryPayload {
  const payload = wrapBareSummaryFields({ ...((sessionSummary ?? {}) as FhirSummaryPayload) });

  for (const [key, read] of TEXT_SECTIONS) {
    const edited = (read(fields) ?? '').trim();
    const original = (read(baseline) ?? '').trim();

    // When the text changed, drop the AI provenance (source, timestamp, explanation...) that
    // described the old text, but keep HPI's `symptom_codes`, which sits beside it.
    if (payload[key] !== undefined) {
      if (edited !== original) {
        const codes = asRecord(payload[key])?.symptom_codes;
        payload[key] = { value: edited, ...(codes !== undefined ? { symptom_codes: codes } : {}) };
      }
    } else if (edited || edited !== original) {
      payload[key] = { value: edited };
    }
  }

  // ICD codes sit beside the HPI text as `symptom_codes`. Use the doctor's
  // Diagnosis-table rows if any, else the AI's codes, else the ones saved in the DB.
  const editedCodes = (fields.diagnosisCodes ?? [])
    .filter((item) => item.symptom.trim())
    .map((item) => ({
      symptom: item.symptom.trim(),
      symptom_id: [item.type?.trim() || 'ICD-10', item.code.trim(), item.description.trim()]
        .join('|')
        .replace(/\|+$/, ''),
    }));
  const hpi = asRecord(payload.history_of_present_illness);
  const aiCodes = Array.isArray(hpi?.symptom_codes) ? hpi.symptom_codes : [];
  const symptomCodes = editedCodes.length
    ? editedCodes
    : aiCodes.length
      ? aiCodes
      : fallbackSymptomCodes;
  if (symptomCodes?.length) {
    payload.history_of_present_illness = { value: '', ...hpi, symptom_codes: symptomCodes };
  }

  // Medications are the one structured section the Case Sheet fully owns — the
  // doctor edits them as a table — so the table always wins, including when it
  // has been emptied: deleted medicines must not come back from the AI's copy.
  // An untouched table keeps the AI's medication objects and their provenance.
  const medications = namedMedications(fields);
  const medicationsUntouched =
    payload.medications !== undefined &&
    JSON.stringify(medications) === JSON.stringify(namedMedications(baseline));
  if (!medicationsUntouched) {
    payload.medications = { value: medications.map(toFhirMedication) };
  }

  return payload;
}
